import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useCallback, useEffect } from 'react';
import { useSiteContext } from '@zooplus/commons-lang';
import { useTranslation } from '../../hooks/use-translations.js';
import { logError } from '../../services/error-service.js';
import { fetchSamples } from '../../services/samples-service.js';
import { trackingSampleAddedToCart, trackingSampleClicked, trackingPromotedSamplesOffer } from '../../utils/tracking.js';
import { SentryErrorBoundary } from '../error-boundary/SentryErrorBoundary.js';
import { FreeSamples } from '../free-samples/FreeSamples.js';
import { SamplesSelector } from '../samples-selector/SamplesSelector.js';

/**
 * React functional component that renders a list of samples products
 * to add to cart in cart overview section
 *
 * @return {JSX.Element} Samples component.
 */
const Samples = ({ initialSample, cartArticlesId, customerId, setFreeSampleArticle, }) => {
    const { site, locale, language } = useSiteContext();
    //const stage = findStageFromUrl() as DeploymentEnv;
    const siteId = site.id;
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(initialSample);
    const [products, setProducts] = useState([]);
    const [campaignId, setCampaignId] = useState();
    const handleConfirm = useCallback(async () => {
        setIsOpen(false);
        if (selectedProduct) {
            await trackingSampleAddedToCart(siteId, customerId, selectedProduct, campaignId);
            setFreeSampleArticle({ articleId: selectedProduct.article_id, quantity: 1 });
        }
    }, [selectedProduct, siteId, customerId, campaignId, setFreeSampleArticle]);
    const handleExitWithoutConfirmation = useCallback(() => {
        setIsOpen(false);
        setSelectedProduct(undefined);
    }, []);
    const handleOnOpen = useCallback(async () => {
        await trackingSampleClicked(siteId, customerId);
        setIsOpen(true);
    }, [siteId, customerId]);
    const handleProductSelect = useCallback((product) => {
        if (!product || selectedProduct?.article_id === product.article_id) {
            setSelectedProduct(undefined);
        }
        else {
            setSelectedProduct(product);
        }
    }, [selectedProduct]);
    useEffect(() => {
        const load = async () => {
            try {
                let samples = [];
                let campaign_id;
                if (cartArticlesId.length && siteId) {
                    const response = await fetchSamples({
                        cartArticles: cartArticlesId,
                        siteId: siteId,
                        language: language,
                        customerId: customerId,
                    });
                    samples = response.data.samples || [];
                    campaign_id = response.data.campaign_id;
                }
                else {
                    samples = [];
                }
                setCampaignId(campaign_id);
                if (samples.length > 0 && campaignId) {
                    await trackingPromotedSamplesOffer(siteId, customerId, samples, campaignId);
                }
                setProducts(samples);
            }
            catch (e) {
                setProducts([]);
                logError(e);
            }
        };
        load();
    }, [cartArticlesId, siteId, locale, language, customerId, campaignId, logError]);
    if (products.length === 0) {
        return null;
    }
    return (jsxs(Fragment, { children: [!selectedProduct && (jsx(FreeSamples, { t: t, handler: handleOnOpen, "data-testid": "samples-component" })), jsx(SamplesSelector, { t: t, isOpen: isOpen, products: products, selectedProduct: selectedProduct, handleExitWithoutConfirmation: handleExitWithoutConfirmation, handleProductSelect: handleProductSelect, handleConfirm: handleConfirm })] }));
};
const WrappedSamples = (props) => {
    return (jsx(SentryErrorBoundary, { children: jsx(Samples, { ...props }) }));
};

export { Samples, WrappedSamples };
//# sourceMappingURL=Samples.js.map
